import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, API, authQuery, normalizeLangCode } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Award, FileText, Download, Eye, X, Sparkles, ScrollText, Trash2 } from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import LodgeDatePicker from "../components/LodgeDatePicker";

const CERT_TYPES = ["buna_conduita", "transfer", "afiliere", "retragere", "adormire", "numire_oficeri", "atestat_onorific", "cerere_transfer"];

// Whether each type expects custom text fields (e.g. "new lodge" or "officer role").
// `labelKey` resolves via t() so the field name auto-localizes.
const CUSTOM_FIELDS = {
  transfer: [{ key: "new_lodge", labelKey: "certificates.field_new_lodge_transfer" }],
  afiliere: [{ key: "new_lodge", labelKey: "certificates.field_new_lodge_afiliere" }],
  retragere: [{ key: "reason", labelKey: "certificates.field_reason_retragere" }],
  adormire: [{ key: "reason", labelKey: "certificates.field_reason_adormire" }],
  numire_oficeri: [{ key: "officer_role", labelKey: "certificates.field_officer_role" }],
  atestat_onorific: [{ key: "officer_role", labelKey: "certificates.field_honorific_role" }],
  cerere_transfer: [
    { key: "brother_name", labelKey: "certificates.field_brother_name", required: true },
    { key: "current_lodge", labelKey: "certificates.field_current_lodge", required: true },
    { key: "degree", labelKey: "certificates.field_degree" },
    { key: "init_date", labelKey: "certificates.field_init_date", placeholder: "DD.MM.YYYY" },
    { key: "passport", labelKey: "certificates.field_passport" },
  ],
};

export default function CertificatesPage() {
  const { t, i18n } = useTranslation();
  const en = i18n.language !== "ro";
  const labelFor = (key) => t(`certificates.type_${key}`);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSecretary = (user?.lodge_office || "").trim() === "Secretar";

  const [members, setMembers] = useState([]);
  const [type, setType] = useState("buna_conduita");
  const [memberId, setMemberId] = useState("");
  const [lang, setLang] = useState(() => {
    const cur = normalizeLangCode(i18n.language || 'ro');
    return cur === 'en' && !(i18n.language || '').startsWith('en') ? 'ro' : cur;
  });
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Signature gating for cerere_transfer — admin must sign on behalf of
  // the petitioner before the PDF is rendered. Stores the pending action
  // ("view"|"download") so we can resume after the pad closes.
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    // Obedience admins (no child_lodge_id) get the FULL roster, including
    // every brother enrolled at every child lodge under their jurisdiction —
    // so they can issue obedience-level certificates for any member of the
    // wider obedience.
    const params = user?.child_lodge_id ? {} : { include_children: true };
    api.get("/members", { params }).then(({ data }) => setMembers(data || [])).catch(() => {});
  }, [isAdmin, user?.child_lodge_id]);

  // Reset custom fields when the type changes.
  useEffect(() => { setCustom({}); }, [type]);

  const requiredFields = CUSTOM_FIELDS[type] || [];

  const validateBeforeGenerate = (action) => {
    const memberOptional = type === "cerere_transfer";
    if (!memberOptional && !memberId) {
      toast.error(t("certificates.select_a_member_first"));
      return false;
    }
    if (memberOptional) {
      const required = (CUSTOM_FIELDS[type] || []).filter((f) => f.required);
      const missing = required.find((f) => !(custom[f.key] || "").trim());
      if (missing) {
        toast.error(t("certificates.field_required", { name: t(missing.labelKey) }));
        return false;
      }
    }
    return true;
  };

  const generate = async (action /* "view" | "download" */) => {
    if (!validateBeforeGenerate(action)) return;
    // For cerere_transfer the petitioner must sign IN-APP before the PDF
    // is rendered (per user spec). The signature pad opens; once the
    // brother taps SAVE, `runGenerate` is called with the base64 PNG.
    if (type === "cerere_transfer") {
      setPendingAction(action);
      return;
    }
    runGenerate(action, null);
  };

  const runGenerate = async (action, signatureB64 /* nullable */) => {
    setBusy(true);
    try {
      const cd = { ...(custom || {}) };
      if (signatureB64) cd.petitioner_signature_b64 = signatureB64;
      const resp = await api.post(
        "/certificates/generate",
        { type, member_id: memberId || null, lang, custom_data: cd },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${labelFor(type).replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      } else {
        setPreviewUrl(url);
      }
      toast.success(t("certificates.certificate_generated"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => `${a.last_name || ""}`.localeCompare(b.last_name || ""));
  }, [members]);

  if (!isAdmin) {
    return (
      <div data-testid="certificates-page">
        <h1 className="page-title">{t("certificates.certificates")}</h1>
        {isSecretary ? (
          <p className="page-subtitle">
            <Link to="/minutes" style={{ textDecoration: "underline" }} data-testid="certificates-minutes-link">
              {t("nav.minutes", "Procese-Verbale")} →
            </Link>
          </p>
        ) : (
          <p className="page-subtitle">{t("certificates.admin_access_required")}</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="certificates-page">
      <div className="page-header" style={{ marginBottom: "1.5rem" }}>
        <h1 className="page-title">{t("certificates.certificates")}</h1>
        <p className="page-subtitle">
          {t("certificates.generate_official_lodge_documents_auto")}
        </p>
      </div>

      <div className="gold-card" style={{ padding: "1.5rem" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-lodge">{t("certificates.document_type")}</label>
            <select
              className="input-lodge"
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="cert-type"
            >
              {CERT_TYPES.map((ct) => (
                <option key={ct} value={ct}>{labelFor(ct)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-lodge">{t("certificates.language")}</label>
            <select
              className="input-lodge"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="cert-lang"
            >
              <option value="ro">🇷🇴 Română</option>
              <option value="en">🇬🇧 English</option>
              <option value="fr">🇫🇷 Français</option>
              <option value="de">🇩🇪 Deutsch</option>
              <option value="it">🇮🇹 Italiano</option>
              <option value="es">🇪🇸 Español</option>
              <option value="pt">🇵🇹 Português</option>
              <option value="pt-BR">🇧🇷 Português (Brasil)</option>
              <option value="sr">🇷🇸 Srpski</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-lodge">
              {t("certificates.member")}
              {type === "cerere_transfer" ? (
                <span className="text-xs ml-2 opacity-70">{t("certificates.optional_type_name_below")}</span>
              ) : (
                " *"
              )}
            </label>
            <select
              className="input-lodge"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              data-testid="cert-member"
            >
              <option value="">{t("certificates.select_member")}</option>
              {sortedMembers.map((m) => {
                const gradeKey = {
                  "Entered Apprentice": "members.grade_entered",
                  "Apprentice": "members.grade_entered",
                  "Ucenic": "members.grade_entered",
                  "Fellow Craft": "members.grade_fellow",
                  "Calfă": "members.grade_fellow",
                  "Calfa": "members.grade_fellow",
                  "Master Mason": "members.grade_master",
                  "Maestru": "members.grade_master",
                  "Honorary": "members.grade_honorary",
                  "Onorific": "members.grade_honorary",
                }[m.grade];
                const gradeLbl = gradeKey ? t(gradeKey, m.grade) : (m.grade || "—");
                return (
                  <option key={m.id} value={m.id}>
                    {m.last_name} {m.first_name} · {gradeLbl}
                  </option>
                );
              })}
            </select>
          </div>
          {requiredFields.map((f) => (
            <div key={f.key} className="md:col-span-2">
              <label className="label-lodge">
                {t(f.labelKey)}{f.required ? " *" : ""}
              </label>
              <input
                className="input-lodge"
                required={!!f.required}
                placeholder={f.placeholder || ""}
                value={custom[f.key] || ""}
                onChange={(e) => setCustom({ ...custom, [f.key]: e.target.value })}
                data-testid={`cert-custom-${f.key}`}
              />
            </div>
          ))}
        </div>

        <div className="gold-divider my-5" />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="btn-outline-gold flex items-center gap-2"
            onClick={() => generate("view")}
            disabled={busy || (type !== "cerere_transfer" && !memberId)}
            data-testid="cert-view-btn"
          >
            <Eye size={14} /> {busy ? "..." : (t("certificates.view"))}
          </button>
          <button
            className="btn-primary-gold flex items-center gap-2"
            onClick={() => generate("download")}
            disabled={busy || (type !== "cerere_transfer" && !memberId)}
            data-testid="cert-download-btn"
          >
            <Download size={14} /> {busy ? "..." : (t("certificates.download_save"))}
          </button>
          <span className="kv-label" style={{ alignSelf: "center", fontSize: "0.7rem", maxWidth: 380 }}>
            {t("certificates.saved_automatically_to_the_member")}
          </span>
        </div>
      </div>


      {previewUrl && (
        <div className="modal-overlay" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <div
            className="modal-content gold-card"
            style={{ maxWidth: 920, height: "85vh", padding: "1rem", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="cert-preview-modal"
          >
            <div className="flex items-center justify-between mb-2">
              <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
                {labelFor(type)}
              </h2>
              <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="text-gray-500" data-testid="close-cert-preview">
                <X size={20} />
              </button>
            </div>
            <iframe
              title="Certificate preview"
              src={previewUrl}
              style={{ flex: 1, border: "1px solid #ead8a8", background: "#fdfcf6" }}
              data-testid="cert-preview-iframe"
            />
          </div>
        </div>
      )}

      {/* Cerere de transfer: petitioner signature pad. The PDF is generated
          ONLY after the brother signs and confirms. */}
      {pendingAction && (
        <div
          className="modal-overlay"
          onClick={() => setPendingAction(null)}
          data-testid="petitioner-signature-overlay"
        >
          <div
            className="modal-content gold-card"
            style={{ maxWidth: 560, padding: "1.2rem" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em", color: "#0d1b2a" }}>
                {t("certificates.petitioner_signature")}
              </h3>
              <button onClick={() => setPendingAction(null)} className="text-gray-500">
                <X size={18} />
              </button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.72rem", marginBottom: 8 }}>
              {t("certificates.the_brother_must_sign_below")}
            </p>
            <SignaturePad
              width={480}
              height={180}
              lang={lang}
              onSave={async (b64) => {
                const action = pendingAction;
                setPendingAction(null);
                await runGenerate(action, b64);
              }}
              onCancel={() => setPendingAction(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
